import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Share2, Twitter, Facebook, Link2, Instagram, X } from 'lucide-react';
import QRCode from 'qrcode';
import { RARITY_UI } from '../lib/rarity';
import { loadBrandLogo, drawBrandLogo } from '../lib/brandLogo';
import { toast } from 'sonner';

/**
 * Universal WildQuest share-card system.
 *
 * Every shareable moment routes through this component. Pass:
 *   moment = {
 *     type: 'first_discovery' | 'new_species' | 'world_first' | 'regional_first'
 *          | 'level_up' | 'achievement' | 'expedition' | 'battle_win'
 *          | 'legendary' | 'mythic' | 'collection_complete',
 *     title:  'string',                    // e.g. "Explorer Begins"
 *     subtitle:'string' (optional),        // "First species discovered"
 *     species: { common_name, scientific_name, rarity, reference_photo_url } | null,
 *     score:   number | string | null,     // Discovery score / battle rating / XP earned
 *     score_label: 'string' (optional),    // "DISCOVERY SCORE"
 *     player:  { name, picture } (optional),
 *     location:'string' (optional, privacy-safe),
 *     accent:  '#hex' (optional — overrides default palette),
 *     share_url: 'https://…',              // URL encoded in QR
 *   }
 */

const MOMENT_LABELS = {
  first_discovery:      'FIRST DISCOVERY',
  new_species:          'NEW SPECIES',
  world_first:          'WORLD FIRST',
  regional_first:       'REGIONAL FIRST',
  level_up:             'LEVEL UP',
  achievement:          'ACHIEVEMENT UNLOCKED',
  expedition:           'EXPEDITION COMPLETE',
  battle_win:           'BATTLE VICTORY',
  legendary:            'LEGENDARY',
  mythic:               'MYTHIC',
  collection_complete:  'COLLECTION COMPLETE',
};

const MOMENT_ACCENTS = {
  first_discovery:     '#E9C46A',
  world_first:         '#FFD166',
  regional_first:      '#8ECAE6',
  level_up:            '#4ADE80',
  achievement:         '#E9C46A',
  legendary:           '#F59E0B',
  mythic:              '#FF6EC7',
  battle_win:          '#EF4444',
  expedition:          '#10B981',
  collection_complete: '#8B5CF6',
  new_species:         '#E9C46A',
};

export default function ShareCardModal({ moment, onClose }) {
  const [shareUrl, setShareUrl] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const canvasRef = useRef(null);

  const accent = moment.accent
    || (moment.species && RARITY_UI[moment.species.rarity]?.color)
    || MOMENT_ACCENTS[moment.type]
    || '#E9C46A';
  const site = window.location.origin;
  const shareLink = moment.share_url || site;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cvs = canvasRef.current;
      if (!cvs) return;
      const W = 1080, H = 1920;
      cvs.width = W; cvs.height = H;
      const ctx = cvs.getContext('2d');

      // Background
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#050b09');
      bg.addColorStop(0.55, '#0b1810');
      bg.addColorStop(1, '#050b09');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // Accent halo
      const halo = ctx.createRadialGradient(W / 2, 720, 80, W / 2, 720, 720);
      halo.addColorStop(0, hexAlpha(accent, 0.32));
      halo.addColorStop(1, 'transparent');
      ctx.fillStyle = halo;
      ctx.fillRect(0, 0, W, H);

      // Diagonal decoration lines
      ctx.strokeStyle = hexAlpha(accent, 0.06);
      ctx.lineWidth = 1;
      for (let i = 0; i < 40; i++) {
        ctx.beginPath();
        ctx.moveTo(0, i * 60);
        ctx.lineTo(W, i * 60 - 320);
        ctx.stroke();
      }

      // Brand row — logo left of wordmark for reinforced identity
      const brandLogo = await loadBrandLogo().catch(() => null);
      // Center the "WildQuest" mark; logo sits immediately left of it.
      ctx.textAlign = 'left';
      ctx.fillStyle = accent;
      ctx.font = 'bold 44px Georgia, "Fraunces", serif';
      const brandText = 'WildQuest';
      const brandTextW = ctx.measureText(brandText).width;
      const logoSize = brandLogo ? 56 : 0;
      const gap = brandLogo ? 14 : 0;
      const totalW = logoSize + gap + brandTextW;
      const startX = (W - totalW) / 2;
      if (brandLogo) drawBrandLogo(ctx, brandLogo, startX, 130 - 46, logoSize);
      ctx.fillText(brandText, startX + logoSize + gap, 130);
      // Sub-label centered under the brand row.
      ctx.textAlign = 'center';
      ctx.fillStyle = '#94a3b8';
      ctx.font = 'bold 26px "Outfit", sans-serif';
      const labelText = MOMENT_LABELS[moment.type] || moment.type.toUpperCase().replace(/_/g, ' ');
      ctx.fillText(labelText, W / 2, 178);

      // Card
      const cardX = 90, cardY = 280, cardW = W - 180, cardH = 1200;
      roundedRect(ctx, cardX, cardY, cardW, cardH, 48);
      ctx.fillStyle = '#0a1610';
      ctx.fill();
      ctx.strokeStyle = accent;
      ctx.lineWidth = 4;
      ctx.stroke();

      // Species image (if provided)
      const imgY = cardY + 24;
      const imgH = 620;
      if (moment.species?.reference_photo_url) {
        try {
          const img = await loadImage(moment.species.reference_photo_url);
          if (cancelled) return;
          ctx.save();
          roundedRect(ctx, cardX + 24, imgY, cardW - 48, imgH, 32);
          ctx.clip();
          drawCoverImage(ctx, img, cardX + 24, imgY, cardW - 48, imgH);
          ctx.restore();
        } catch {
          drawImagePlaceholder(ctx, cardX + 24, imgY, cardW - 48, imgH, accent);
        }
      } else {
        drawImagePlaceholder(ctx, cardX + 24, imgY, cardW - 48, imgH, accent);
      }

      // Rarity chip (top-left over image)
      if (moment.species?.rarity) {
        const chip = (moment.species.rarity || '').toUpperCase();
        ctx.font = 'bold 26px "Outfit", sans-serif';
        const chipW = ctx.measureText(chip).width + 40;
        roundedRect(ctx, cardX + 44, imgY + 24, chipW, 44, 22);
        ctx.fillStyle = hexAlpha('#000000', 0.65);
        ctx.fill();
        ctx.strokeStyle = accent; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = accent;
        ctx.textAlign = 'left';
        ctx.fillText(chip, cardX + 44 + 20, imgY + 24 + 30);
      }

      // Title (moment.title) below image
      ctx.textAlign = 'left';
      ctx.fillStyle = '#F9FAFB';
      ctx.font = 'bold 74px Georgia, "Fraunces", serif';
      const titleLines = wrapText(ctx, moment.title || moment.species?.common_name || 'Discovery', cardW - 120, 74);
      let ty = imgY + imgH + 80;
      titleLines.slice(0, 2).forEach(l => { ctx.fillText(l, cardX + 60, ty); ty += 82; });

      // Scientific / subtitle
      if (moment.subtitle || moment.species?.scientific_name) {
        ctx.fillStyle = '#94a3b8';
        ctx.font = 'italic 34px Georgia, "Fraunces", serif';
        ctx.fillText(moment.subtitle || moment.species.scientific_name, cardX + 60, ty);
        ty += 52;
      }

      // Score display
      if (moment.score !== undefined && moment.score !== null) {
        ctx.textAlign = 'center';
        ctx.fillStyle = accent;
        ctx.font = 'bold 132px "JetBrains Mono", "Menlo", monospace';
        ctx.fillText(String(moment.score), W / 2, cardY + cardH - 130);
        ctx.fillStyle = '#94a3b8';
        ctx.font = 'bold 26px "Outfit", sans-serif';
        ctx.fillText(moment.score_label || 'DISCOVERY SCORE', W / 2, cardY + cardH - 88);
      }

      // Player + location strip (below the card)
      ctx.textAlign = 'left';
      const playerY = cardY + cardH + 90;
      if (moment.player?.name) {
        // Optional avatar circle
        if (moment.player.picture) {
          try {
            const av = await loadImage(moment.player.picture);
            if (!cancelled) {
              ctx.save();
              ctx.beginPath();
              ctx.arc(180, playerY - 8, 44, 0, Math.PI * 2);
              ctx.closePath();
              ctx.clip();
              ctx.drawImage(av, 136, playerY - 52, 88, 88);
              ctx.restore();
              ctx.strokeStyle = accent; ctx.lineWidth = 3;
              ctx.beginPath(); ctx.arc(180, playerY - 8, 44, 0, Math.PI * 2); ctx.stroke();
            }
          } catch {}
        }
        ctx.fillStyle = '#F9FAFB';
        ctx.font = 'bold 36px "Outfit", sans-serif';
        ctx.fillText(moment.player.name, 250, playerY - 16);
        if (moment.location) {
          ctx.fillStyle = '#94a3b8';
          ctx.font = '28px "Outfit", sans-serif';
          ctx.fillText(moment.location, 250, playerY + 22);
        }
      }

      // Date
      ctx.textAlign = 'right';
      ctx.fillStyle = '#94a3b8';
      ctx.font = '30px "Outfit", sans-serif';
      const dateStr = new Date(moment.date || Date.now()).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      ctx.fillText(dateStr, W - 260, playerY - 10);

      // QR code — bottom-right
      try {
        const qrUrl = await QRCode.toDataURL(shareLink, {
          margin: 0, width: 180, color: { dark: '#040A08', light: '#F9FAFB' },
        });
        const qr = await loadImage(qrUrl);
        const qx = W - 240, qy = H - 260;
        // white plate
        roundedRect(ctx, qx - 12, qy - 12, 204, 204, 20);
        ctx.fillStyle = '#F9FAFB'; ctx.fill();
        ctx.drawImage(qr, qx, qy, 180, 180);
      } catch {}

      // Bottom slogan
      ctx.textAlign = 'left';
      ctx.fillStyle = accent;
      ctx.font = 'bold 36px "Outfit", sans-serif';
      ctx.fillText('Explore Nature.', 90, H - 170);
      ctx.fillStyle = '#F9FAFB';
      ctx.fillText('Discover Wildlife.', 90, H - 126);
      ctx.fillStyle = '#94a3b8';
      ctx.font = '26px "Outfit", sans-serif';
      ctx.fillText(site.replace(/^https?:\/\//, ''), 90, H - 82);

      if (!cancelled) setShareUrl(cvs.toDataURL('image/png'));
    })();
    return () => { cancelled = true; };
  }, [moment, accent, shareLink, site]);

  const download = () => {
    if (!shareUrl) return toast.error('Card still rendering');
    setDownloading(true);
    const filename = `wildquest-${(moment.type || 'discovery')}-${Date.now()}.png`;
    const a = document.createElement('a');
    a.href = shareUrl; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setDownloading(false);
    toast.success('Card saved to your device');
  };

  const nativeShare = async () => {
    if (!shareUrl) return toast.error('Card still rendering');
    if (!navigator.canShare) return toast.error('Native share unavailable — use Download');
    try {
      const blob = await (await fetch(shareUrl)).blob();
      const file = new File([blob], 'wildquest.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `WildQuest — ${moment.title}`,
          text: `${moment.title} on WildQuest — join me!`,
        });
      }
    } catch {}
  };

  const openIntent = (kind) => {
    const text = encodeURIComponent(`${moment.title} on WildQuest`);
    const url = encodeURIComponent(shareLink);
    const urls = {
      x:        `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
      threads:  `https://www.threads.net/intent/post?text=${text}%20${url}`,
      bluesky:  `https://bsky.app/intent/compose?text=${text}%20${url}`,
    };
    window.open(urls[kind], '_blank', 'noopener,noreferrer');
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareLink);
    toast.success('Link copied to clipboard');
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="share-card-modal"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[105] overflow-y-auto"
        style={{ background: 'rgba(4,10,8,0.97)' }}
      >
        <div className="max-w-md mx-auto min-h-screen px-6 pt-12 pb-10 space-y-5 relative">
          <button data-testid="share-card-close" onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>

          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.35em]" style={{ color: accent }}>
              {MOMENT_LABELS[moment.type] || moment.type}
            </p>
            <h2 className="font-serif text-3xl mt-2">Share the moment</h2>
            <p className="text-xs text-muted-foreground mt-1">A beautiful card, ready for your feed.</p>
          </div>

          <div className="rounded-3xl overflow-hidden border border-white/10 bg-black">
            {shareUrl ? (
              <img src={shareUrl} alt="Share card" className="w-full block" data-testid="share-card-preview" />
            ) : (
              <div className="aspect-[9/16] flex items-center justify-center text-xs text-muted-foreground">
                Rendering share card…
              </div>
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" aria-hidden />

          <div className="grid grid-cols-2 gap-2">
            <ShareBtn testid="share-download"  onClick={download}   icon={Download}  label="Download" primary disabled={downloading} />
            <ShareBtn testid="share-native"    onClick={nativeShare} icon={Share2}   label="Share…" primary />
            <ShareBtn testid="share-x"         onClick={() => openIntent('x')}        icon={Twitter} label="X (Twitter)" />
            <ShareBtn testid="share-facebook"  onClick={() => openIntent('facebook')} icon={Facebook} label="Facebook" />
            <ShareBtn testid="share-instagram" onClick={download}    icon={Instagram} label="Instagram (download)" />
            <ShareBtn testid="share-copy"      onClick={copyLink}    icon={Link2}     label="Copy Link" />
          </div>

          <button data-testid="share-card-done" onClick={onClose}
            className="w-full py-3.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs uppercase tracking-widest">
            Keep exploring
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function ShareBtn({ testid, onClick, icon: Icon, label, primary, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex items-center justify-center gap-2 py-3 rounded-2xl text-xs font-medium transition-colors disabled:opacity-40 ${
        primary ? 'bg-accent text-black hover:brightness-105' : 'bg-white/5 hover:bg-white/10 border border-white/10'
      }`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
    </button>
  );
}

// ---- Canvas helpers ----------------------------------------------------------
function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawCoverImage(ctx, img, dx, dy, dw, dh) {
  const iw = img.naturalWidth, ih = img.naturalHeight;
  const targetAR = dw / dh, sourceAR = iw / ih;
  let sx = 0, sy = 0, sw = iw, sh = ih;
  if (sourceAR > targetAR) { sw = ih * targetAR; sx = (iw - sw) / 2; }
  else                     { sh = iw / targetAR; sy = (ih - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

function drawImagePlaceholder(ctx, x, y, w, h, accent) {
  ctx.save();
  roundedRect(ctx, x, y, w, h, 32);
  ctx.fillStyle = hexAlpha(accent, 0.1);
  ctx.fill();
  ctx.strokeStyle = hexAlpha(accent, 0.35);
  ctx.setLineDash([16, 12]);
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.restore();
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(' ');
  const lines = [];
  let current = '';
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function hexAlpha(hex, alpha) {
  // Convert #RRGGBB to rgba(r,g,b,a)
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}
